import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  LoadCanvasTemplateNoReload,
  loadCaptchaEnginge,
  validateCaptcha,
} from "react-simple-captcha";

import Text from "../../text/Text";
import { Input } from "../../input/Input";
import {
  authenticate,
  captchaCode,
  captchaCodePlaceholder,
  captchaSubHeader,
  carrierRateMsg,
  invalidCaptchaCode,
  mobile,
  otpNotReceived,
  otpWillExpireMsg,
  resend,
  submit,
  twoFactorAuthenticationSubHeader,
  twoFactorVerificationMessage,
  twoStepVerification,
  verify,
  verifyingHuman,
} from "../../messages";
import {
  callAPIInterface,
  captchaDigit,
  concatenatedMobileNumber,
  getCountryObj,
  getCurrentTimeInSecond,
  mobileNumberWithoutCallingCode,
  validatePhoneNumber,
  captchaBackgroundColor,
} from "../../constants";
import PhoneNumberInput from "../../phoneNumberInput/PhoneNumberInput";
import { useSession } from "../../../hooks/useSession";
import Button from "../../button/Button";
import { OTPInput } from "../../otpInput/OTPInput";
import { CustomLink } from "../../link/Link";
import { store } from "../../../redux/store";
import { showToast } from "../../../redux/actions";
import { isValidPhoneNumber } from "react-phone-number-input/input";
import DynamicHeight from "../../DynamicHeight";
import CustomDivider from "../../divider/Divider";
import CommonDrawer from "../../drawer/CommonDrawer";
import { Update } from "@mui/icons-material";

const TwoFactorDrawer = ({
  openTwoFactorModal,
  setOpenTwoFactorModal,
  getMfaStatus,
  mfaData,
  phoneNumber,
  callingCode,
  isIosDevice,
}) => {
  const [step, setStep] = useState(1);
  const [captcha, setCaptcha] = useState("");
  const [mobileNo, setMobileNo] = useState();
  const [otp, setOtp] = useState("");
  const [countries, setCountries] = useState([]);
  const [isInvalidCaptcha, setIsInvalidCaptcha] = useState(false);
  const [timerCount, setTimerCount] = useState(0);
  const [mobileDialCode, setMobileDialCode] = useState();
  const [countryIsoCode, setCountryIsoCode] = useState();
  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const { session } = useSession();
  DynamicHeight({ isIosDevice });

  const userCountry = session?.user?.country;
  const mfaEnabled = mfaData?.is_mfa_phone_number_verified;

  const setTimerStates = (timerExpireAt) => {
    const currentSeconds = getCurrentTimeInSecond(timerExpireAt);
    setTimerCount(currentSeconds);
    currentSeconds === 0 && setIsOtpExpired(true);
  };

  const handleVerifyCaptcha = () => {
    setIsSubmitting(true);
    const isValid = validateCaptcha(captcha);
    if (isValid) {
      setIsInvalidCaptcha(false);
      if (mobileDialCode && mobileNo) {
        handleSendOTP();
      } else {
        setStep(2);
      }
    } else {
      setIsInvalidCaptcha(true);
    }
    setIsSubmitting(false);
  };

  const handleReloadCaptcha = () => {
    loadCaptchaEnginge(captchaDigit, captchaBackgroundColor);
    setCaptcha("");
  };

  const handleVerifyOtp = () => {
    setIsSubmitting(true);
    const apiData = {
      otp: otp,
    };
    let apiPath = mfaEnabled
      ? "/mfa/phone-number/remove/verify"
      : "/mfa/phone-number/verify";

    callAPIInterface("POST", apiPath, apiData)
      .then((res) => {
        getMfaStatus();
        setOpenTwoFactorModal(false);
      })
      .catch((_e) => {})
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const getCallingCodes = async () => {
    try {
      const codes = await callAPIInterface("GET", "/mfa/calling-code");
      setCountries(codes.country_calling_codes);
    } catch (_e) {}
  };

  const handleSendOTP = () => {
    const isValidPhoneNumber = validatePhoneNumber(mobileNo);
    if (isValidPhoneNumber) {
      setIsSubmitting(true);
      const apiMethod = mfaEnabled && phoneNumber ? "DELETE" : "POST";
      const apiPath =
        mfaEnabled && phoneNumber ? `/mfa/${mfaData?.id}` : "/mfa/phone-number";
      const apiData =
        mfaEnabled && phoneNumber
          ? ""
          : {
              mfa_calling_code: mobileDialCode,
              mfa_phone_number: mobileNumberWithoutCallingCode(
                mobileDialCode,
                mobileNo
              ),
            };
      callAPIInterface(apiMethod, apiPath, apiData)
        .then((res) => {
          setTimerCount(getCurrentTimeInSecond(res?.otp_expires_at));
          setOtpExpiresAt(res?.otp_expires_at);
          setStep(3);
        })
        .catch((_e) => {})
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      store.dispatch(
        showToast({
          isToastOpen: true,
          toastMessage: "Please enter a valid mobile number",
          toastVariant: "error",
        })
      );
    }
  };

  const handleResendOTP = async () => {
    const apiPath = mfaEnabled
      ? "/mfa/phone-number/remove/resend-verification-code"
      : "/mfa/phone-number/resend-verification-code";
    try {
      const res = await callAPIInterface("POST", apiPath);
      setOtp("");
      const otpRef = document.querySelector(".otp-input");
      otpRef.focus();
      setIsOtpExpired(false);
      setTimerCount(getCurrentTimeInSecond(res?.otp_expires_at));
      setOtpExpiresAt(res?.otp_expires_at);
    } catch (e) {}
  };

  const modalHeader = step === 1 ? verifyingHuman : twoStepVerification;

  const renderModalFooter = () => {
    switch (step) {
      case 1:
        return (
          <Button
            loading={isSubmitting}
            variant="contained"
            sx={{
              borderRadius: "4px !important",
              width: "100%",
            }}
            label={verify}
            onClick={handleVerifyCaptcha}
          />
        );
      case 2:
        return (
          <Button
            loading={isSubmitting}
            variant="contained"
            sx={{
              borderRadius: "4px !important",
              width: "100%",
            }}
            label={submit}
            onClick={handleSendOTP}
            disabled={mobileNo ? !isValidPhoneNumber(mobileNo) : true}
          />
        );
      case 3:
        return (
          <>
            <Button
              loading={isSubmitting}
              variant="contained"
              sx={{
                borderRadius: "4px !important",
                textTransform: "capitalize",
              }}
              fullWidth
              disabled={otp.length !== 6}
              label={authenticate}
              onClick={handleVerifyOtp}
            />
          </>
        );
      default:
        <></>;
    }
  };

  const renderModalBody = () => {
    switch (step) {
      case 1:
        return (
          <Box className="two-fa-setup">
            <Text className="heading-title">{modalHeader}</Text>
            <Text className="heading-subtitle">{captchaSubHeader}</Text>
            <Box className="captcha-container-box" margin="20px 0">
              <LoadCanvasTemplateNoReload />
            </Box>
            <Box className="reload-captcha">
              <Text onClick={handleReloadCaptcha} font="bold" fontSize={14}>
                <Update />
                Reload Captcha
              </Text>
            </Box>
            <Input
              fullWidth
              showLabel
              value={captcha}
              label={captchaCode}
              onChange={(e) => {
                setIsInvalidCaptcha(false);
                setCaptcha(e.target.value);
              }}
              type="text"
              inputProps={{ maxLength: captchaDigit }}
              customClass="mt-24"
              placeholder={captchaCodePlaceholder}
            />
            {isInvalidCaptcha && (
              <Text
                className="error-text"
                size={16}
                font="regular"
                sx={{ textAlign: "left", marginTop: "5px" }}
              >
                {invalidCaptchaCode}
              </Text>
            )}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Text className="heading-title">{modalHeader}</Text>
            <Text className="heading-subtitle">
              {twoFactorAuthenticationSubHeader}
            </Text>
            <PhoneNumberInput
              onOpen={() => {
                document.body.style.overflow = "hidden";
              }}
              onClose={() => {
                document.body.style.overflow = "auto";
              }}
              name="mobileNo"
              options={countries}
              error={
                touched && Boolean(mobileNo) && !isValidPhoneNumber(mobileNo)
              }
              selectValue={mobileDialCode}
              MenuProps={{
                id: "settings-mfa-select-calling-code",
                disableScrollLock: true,
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
              countryIsoCode={countryIsoCode}
              inputValue={mobileNo}
              label={mobile}
              showLabel={true}
              onChangeSelect={(value) => {
                setMobileNo("");
                setMobileDialCode(
                  typeof value === "object" ? value.calling_code : value
                );
              }}
              onBlur={() => setTouched(true)}
              onChange={(value) => {
                setMobileNo(value);
                if (touched) setTouched(false); // Reset touched if user starts editing again
              }}
              boxClassName="mt-24"
            />
            <Text
              variant="subtitle1"
              font="regular"
              size={14}
              className="grey-text mt-8"
            >
              {carrierRateMsg}
            </Text>
          </Box>
        );
      case 3:
        return (
          <Box>
            <Text className="heading-title">{modalHeader}</Text>
            <Text className="heading-subtitle">
              {twoFactorVerificationMessage[0]}{" "}
              <span style={{ fontWeight: "bold", color: "#0A193E" }}>
                {mobileNo.slice(-4)}.
              </span>
            </Text>
            <OTPInput
              value={otp}
              onChange={(val) => setOtp(val)}
              numInputs={6}
              containerStyle={{
                marginTop: "20px",
                gap: "3%",
              }}
            />
            {timerCount > 0 && !isOtpExpired ? (
              <Text
                sx={{ color: "#667085" }}
                className="mt-16"
                size={14}
                align="center"
              >
                {otpWillExpireMsg}{" "}
                <Text sx={{ color: "#0A0A0A" }} component="span" font="bold">
                  {`${Math.floor(timerCount / 60)}`.padStart(2, 0)}:
                  {`${timerCount % 60}`.padStart(2, 0)}
                </Text>
              </Text>
            ) : (
              <Text className="default-text mt-16" size={14} align="center">
                {otpNotReceived}{" "}
                <CustomLink bold onClick={handleResendOTP} size="small">
                  {resend}
                </CustomLink>
              </Text>
            )}
          </Box>
        );
      default:
        return <></>;
    }
  };
  useEffect(() => {
    setTimeout(() => {
      loadCaptchaEnginge(captchaDigit, captchaBackgroundColor);
    }, 0);
    getCallingCodes();
  }, []);

  useEffect(() => {
    let countryObj = getCountryObj("calling_code", mobileDialCode);
    setCountryIsoCode(countryObj?.iso2);
  }, [mobileDialCode]);

  useEffect(() => {
    const countryObj = getCountryObj("short_name", userCountry);
    setCountryIsoCode(countryObj?.iso2);
    if (phoneNumber && callingCode) {
      setMobileNo(concatenatedMobileNumber(callingCode, phoneNumber));
      setMobileDialCode(callingCode);
    } else {
      setMobileDialCode(countryObj?.calling_code);
    }
  }, [session]);

  useEffect(() => {
    if (otpExpiresAt && timerCount > 0) {
      const timer = setInterval(() => {
        setTimerStates(otpExpiresAt);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [otpExpiresAt, timerCount]);

  return (
    <CommonDrawer
      anchor="bottom"
      open={openTwoFactorModal}
      onClose={() => setOpenTwoFactorModal(false)}
      className="two-factor-drawer"
    >
      <Box className="add-contact-drawer-content">
        <Box className="drawer-header">
          <ArrowBackIcon
            onClick={() => {
              setOpenTwoFactorModal(false);
            }}
          ></ArrowBackIcon>
        </Box>
        <Box className="drawer-body">{renderModalBody()}</Box>
        <CustomDivider />
        <Box className="drawer-footer">{renderModalFooter()}</Box>
      </Box>
    </CommonDrawer>
  );
};

export default TwoFactorDrawer;
